/**
 * Grid commands — sherwood grid start|status
 *
 * Runs the ATR-based grid trading strategy as a standalone loop,
 * parallel to the directional agent. Isolated capital and portfolio.
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { createInterface } from 'node:readline/promises';
import { GridLoop } from '../grid/loop.js';
import { GridPortfolio } from '../grid/portfolio.js';
import { GridHedgeManager } from '../grid/hedge.js';
import { OnchainGridExecutor } from '../grid/onchain-executor.js';
import { HyperliquidProvider } from '../providers/data/hyperliquid.js';
import { DEFAULT_GRID_CONFIG } from '../grid/config.js';
import { runBacktest } from '../grid/backtest.js';
import type { BacktestResult } from '../grid/backtest.js';
import type { GridConfig } from '../grid/config.js';
import { runSweep, type SweepResult, type SweepableField } from '../grid/sweep.js';

/**
 * Default reason strings recorded on `state.pauseReason` when an operator
 * halts a grid. Stable strings so log-grep / dashboard filters can match
 * operator-initiated halts deterministically.
 */
const DEFAULT_PAUSE_REASON = 'Manually paused by operator';
const DEFAULT_FLATTEN_REASON = 'Manually flattened by operator';

/** Seconds to wait at the y/N confirmation prompt before aborting. */
const CONFIRM_TIMEOUT_SECONDS = 60;

const DIM = chalk.gray;
const G = chalk.green;
const BOLD = chalk.white.bold;
const W = chalk.white;
const SEP = () => console.log(DIM('─'.repeat(60)));

function printSummary(r: BacktestResult): void {
  const dd = r.drawdown;
  const days = r.window.days;
  const rtPerDay = (r.totals.roundTrips / Math.max(days, 1)).toFixed(1);
  const fillPerDay = (r.totals.fills / Math.max(days, 1)).toFixed(1);
  const pnlSign = r.capital.pnlUsd >= 0 ? '+' : '-';
  const pnlAbs = Math.abs(r.capital.pnlUsd).toFixed(2);
  const pnlPctStr = (r.capital.pnlPct * 100).toFixed(2);

  console.log();
  console.log(G.bold(`  Grid Backtest — ${r.runId}`));
  console.log(DIM('─'.repeat(64)));
  console.log(W(`  Window:        ${r.window.fromIso.slice(0, 10)} → ${r.window.toIso.slice(0, 10)}  (${days.toFixed(0)} days)`));
  console.log(W(`  Capital:       $${r.capital.initialUsd.toLocaleString()} → $${r.capital.finalUsd.toFixed(2)}  (${pnlSign}$${pnlAbs}, ${pnlSign}${pnlPctStr}%)`));
  console.log(W(`  Gross PnL:     $${r.capital.grossPnlUsd.toFixed(2)} (before fees)`));
  console.log(W(`  Fees (${r.fees.bps}bps): -$${r.fees.totalUsd.toFixed(2)}  ($${r.fees.perFill.toFixed(4)}/fill)`));
  if (r.hedge.enabled) {
    const hedgeNet = r.hedge.realizedPnlUsd + r.hedge.unrealizedPnlUsd;
    const hedgeSign = hedgeNet >= 0 ? '+' : '-';
    const hedgeAbs = Math.abs(hedgeNet).toFixed(2);
    console.log(W(`  Hedge:         ${hedgeSign}$${hedgeAbs} (realized $${r.hedge.realizedPnlUsd.toFixed(2)} + unrealized $${r.hedge.unrealizedPnlUsd.toFixed(2)}, ${r.hedge.adjustments} adj)`));
  } else {
    console.log(W(`  Hedge:         disabled`));
  }
  console.log(W(`  Round trips:   ${r.totals.roundTrips}  (${rtPerDay}/day)`));
  console.log(W(`  Fills:         ${r.totals.fills}  (${fillPerDay}/day)`));
  console.log(W(`  Rebuilds:      ${r.totals.rebuilds}`));
  console.log(W(`  Max drawdown:  -$${dd.maxUsd.toFixed(2)} (-${(dd.maxPct * 100).toFixed(2)}%)`));
  console.log(W(`  Paused:        ${r.totals.pausedSteps} steps (${((r.totals.pausedSteps / Math.max(r.totals.totalSteps, 1)) * 100).toFixed(1)}%)`));
  console.log(W(`  Skipped:       ${r.totals.skippedSteps} steps`));
  console.log();
  console.log(BOLD('  Per token:'));
  for (const t of r.perToken) {
    const tokPnl = t.pnlUsd >= 0 ? G(`+$${t.pnlUsd.toFixed(2)}`) : chalk.red(`-$${Math.abs(t.pnlUsd).toFixed(2)}`);
    console.log(W(`    ${t.token.padEnd(10)} $${t.allocation.initial.toFixed(0).padStart(6)} → $${t.allocation.final.toFixed(0).padStart(6)}  ${tokPnl}  RTs=${t.roundTrips}  fills=${t.fills}`));
  }
  if (r.liquidations.events.length > 0) {
    console.log();
    console.log(BOLD('  Liquidations:'));
    for (const ev of r.liquidations.events) {
      const date = new Date(ev.timestamp).toISOString().slice(0, 10);
      console.log(W(`    ${ev.token.padEnd(10)} ${date}  unrealized=$${ev.unrealizedPnlAtLiquidation.toFixed(0)}  threshold=$${ev.thresholdUsd.toFixed(0)}`));
    }
    if (r.liquidations.haltedAt !== null) {
      const haltDate = new Date(r.liquidations.haltedAt).toISOString().slice(0, 10);
      console.log(chalk.red.bold(`    RUN HALTED on ${haltDate} — all tokens liquidated.`));
    }
  }
  console.log();
  console.log(W(`  Wall time:     ${(r.durationMs / 1000).toFixed(1)}s`));
  console.log(DIM('─'.repeat(64)));
}

function printSweepSummary(s: SweepResult): void {
  const days = s.window.days;
  console.log();
  console.log(G.bold(`  Grid Sweep — ${s.sweepId}`));
  console.log(DIM('─'.repeat(96)));
  console.log(W(`  Window:    ${new Date(s.window.fromMs).toISOString().slice(0, 10)} → ${new Date(s.window.toMs).toISOString().slice(0, 10)}  (${days.toFixed(0)} days)`));
  console.log(W(`  Tokens:    ${s.tokens.join(', ')}`));
  console.log(W(`  Capital:   $${s.capital.toLocaleString()}`));
  console.log(W(`  Fees:      ${s.feeBps} bps/fill`));
  console.log(W(`  Runs:      ${s.runs.length}`));
  console.log(W(`  Wall:      ${(s.durationMs / 1000).toFixed(1)}s`));
  console.log(DIM('─'.repeat(96)));
  console.log();
  console.log(BOLD('  Rank  Surv  Lev  Levels  ATR×  Drift  RTs    NetPnL$       NetPnL%   DD%      Risk-Adj'));
  console.log(DIM('  ' + '─'.repeat(102)));
  for (let i = 0; i < s.runs.length; i++) {
    const r = s.runs[i]!;
    const rank = String(i + 1).padStart(4);
    const surv = r.survived ? G(' ✓ ') : chalk.red(' ✗ ');
    const lev = String(r.config.leverage ?? '').padStart(3);
    const lvls = String(r.config.levelsPerSide ?? '').padStart(6);
    const atr = String(r.config.atrMultiplier ?? '').padStart(4);
    const drift = String(r.config.rebalanceDriftPct ?? '').padStart(5);
    const rts = String(r.totals.roundTrips).padStart(5);
    const netPnl = `$${r.capital.pnlUsd.toFixed(0)}`.padStart(11);
    const netPct = `${(r.capital.pnlPct * 100).toFixed(1)}%`.padStart(8);
    const ddPct = `${(r.drawdown.maxPct * 100).toFixed(1)}%`.padStart(8);
    const ra = r.riskAdjusted.toFixed(2).padStart(8);
    const pnlColor = r.capital.pnlUsd >= 0 ? G : chalk.red;
    console.log(`  ${rank}  ${surv}  ${lev}  ${lvls}  ${atr}  ${drift}  ${rts}  ${pnlColor(netPnl)}  ${pnlColor(netPct)}  ${ddPct}  ${ra}`);
  }
  console.log(DIM('  ' + '─'.repeat(102)));
  console.log();
  console.log(W(`  Saved: ${s.sweepId}/sweep.json + per-run JSONs in ~/.sherwood/grid/sweeps/`));
  console.log();
}

/**
 * Parse `--asset-indices` flag value (e.g. `bitcoin=0,ethereum=1,solana=5`)
 * into `{ bitcoin: 0, ethereum: 1, solana: 5 }`. Validates that each value
 * is a non-negative integer so a typo doesn't quietly become `NaN` in the
 * encoded cancel/place tx.
 */
function parseAssetIndices(raw: string): Record<string, number> {
  const indices: Record<string, number> = {};
  for (const pair of raw.split(',')) {
    const [tok, idx] = pair.split('=');
    if (!tok || !idx) {
      throw new Error(`Bad asset-indices pair: '${pair}' (expected token=index)`);
    }
    const n = Number(idx.trim());
    if (!Number.isInteger(n) || n < 0) {
      throw new Error(`Bad asset-indices value for '${tok}': '${idx}' (must be a non-negative integer)`);
    }
    indices[tok.trim()] = n;
  }
  return indices;
}

function parseTokenSplit(raw: string, tokens: string[]): Record<string, number> {
  const split: Record<string, number> = {};
  for (const pair of raw.split(',')) {
    const [tok, w] = pair.split('=');
    if (!tok || !w) throw new Error(`Bad token-split pair: '${pair}' (expected token=weight)`);
    const weight = Number(w.trim());
    if (!Number.isFinite(weight) || weight < 0) {
      throw new Error(`Bad token-split weight for '${tok}': '${w}'`);
    }
    split[tok.trim()] = weight;
  }
  for (const t of tokens) {
    if (!(t in split)) {
      throw new Error(`--token-split missing weight for '${t}'`);
    }
  }
  const sum = Object.values(split).reduce((a, b) => a + b, 0);
  if (Math.abs(sum - 1.0) > 1e-6) {
    throw new Error(`--token-split weights must sum to 1.0, got ${sum.toFixed(6)}`);
  }
  return split;
}

export function registerGridCommand(program: Command): void {
  const grid = program
    .command('grid')
    .description('Grid trading strategy — ATR-based grid on BTC/ETH/SOL');

  // ── grid start ──
  grid
    .command('start')
    .description('Start the grid trading loop')
    .option('--capital <usd>', 'Starting capital in USD', '5000')
    .option('--cycle <seconds>', 'Cycle interval in seconds', '60')
    .option('--tokens <list>', 'Comma-separated token list', 'bitcoin,ethereum,solana')
    .option('--leverage <n>', 'Leverage multiplier', String(DEFAULT_GRID_CONFIG.leverage))
    .option('--levels <n>', 'Grid levels per side', String(DEFAULT_GRID_CONFIG.levelsPerSide))
    .option('--live', 'enable live execution (places real orders on Hyperliquid)')
    .option('--asset-indices <pairs>', 'comma-separated token=index pairs (mainnet: bitcoin=0,ethereum=1,solana=5)')
    .option('--strategy <address>', 'on-chain strategy contract address (enables on-chain executor)')
    .option('--token-split <pairs>', 'Comma-separated token=weight pairs (must sum to 1.0). Default: equal-weight.')
    .option('--state-dir <path>', 'Override directory for portfolio.json / hedge.json / cycles.jsonl / onchain-state.json. Defaults to ~/.sherwood/grid. Use a separate dir to run paper and live grids in parallel.')
    .action(async (opts) => {
      const capital = parseFloat(opts.capital);
      const cycleMs = parseInt(opts.cycle, 10) * 1000;
      const tokens = opts.tokens.split(',').map((t: string) => t.trim());
      const leverage = parseFloat(opts.leverage);
      const levels = parseInt(opts.levels, 10);
      const stateDir = opts.stateDir as string | undefined;

      const live = !!opts.live;
      let assetIndices: Record<string, number> | undefined;
      if (live) {
        if (!opts.assetIndices) {
          throw new Error('--asset-indices required when --live (mainnet: --asset-indices bitcoin=0,ethereum=1,solana=5)');
        }
        assetIndices = parseAssetIndices(opts.assetIndices as string);
      }

      const strategyAddress = opts.strategy as `0x${string}` | undefined;
      if (strategyAddress && !live) {
        throw new Error('--strategy requires --live');
      }

      // Build token split (explicit if provided, else equal-weight)
      let tokenSplit: Record<string, number>;
      if (opts.tokenSplit) {
        tokenSplit = parseTokenSplit(opts.tokenSplit as string, tokens);
      } else {
        const weight = 1 / tokens.length;
        tokenSplit = {};
        for (const t of tokens) {
          tokenSplit[t] = weight;
        }
      }

      console.log();
      console.log(G.bold('  Grid Strategy'));
      SEP();
      console.log(W(`  Capital:   $${capital.toLocaleString()}`));
      console.log(W(`  Cycle:     ${opts.cycle}s`));
      console.log(W(`  Tokens:    ${tokens.join(', ')}`));
      console.log(W(`  Leverage:  ${leverage}x`));
      console.log(W(`  Levels:    ${levels}/side`));
      SEP();

      const loop = new GridLoop({
        capital,
        cycle: cycleMs,
        live,
        assetIndices,
        strategyAddress,
        stateDir,
        config: {
          ...DEFAULT_GRID_CONFIG,
          tokens,
          leverage,
          levelsPerSide: levels,
          tokenSplit,
        },
      });

      await loop.start();
    });

  // ── grid status ──
  grid
    .command('status')
    .description('Show current grid portfolio status')
    .option('--state-dir <path>', 'Read state from a custom directory (defaults to ~/.sherwood/grid)')
    .action(async (opts) => {
      const portfolio = new GridPortfolio(opts.stateDir as string | undefined);
      const state = await portfolio.load();

      if (!state) {
        console.log(DIM('\n  No grid portfolio found. Run `sherwood grid start` first.\n'));
        return;
      }

      // Reset daily stats if needed before display
      portfolio.resetDailyStats(state);
      const agg = portfolio.aggregateStats(state);

      console.log();
      console.log(G.bold('  Grid Portfolio'));
      SEP();
      console.log(W(`  Allocation:    $${state.totalAllocation.toLocaleString()}`));
      console.log(W(`  Status:        ${state.paused ? chalk.red('PAUSED — ' + state.pauseReason) : G('Active')}`));
      console.log(W(`  Initialized:   ${new Date(state.initializedAt).toLocaleString()}`));
      SEP();

      // Per-token table
      console.log();
      console.log(BOLD('  Token          Alloc       RTs    PnL         Today PnL   Fills'));
      console.log(DIM('  ' + '─'.repeat(72)));

      for (const g of state.grids) {
        const name = g.token.padEnd(13);
        const alloc = `$${g.allocation.toFixed(0)}`.padStart(8);
        const rts = String(g.stats.totalRoundTrips).padStart(6);
        const pnl = `$${g.stats.totalPnlUsd.toFixed(2)}`.padStart(10);
        const todayPnl = `$${g.stats.todayPnlUsd.toFixed(2)}`.padStart(10);
        const fills = String(g.stats.totalFills).padStart(6);
        const pnlColor = g.stats.totalPnlUsd >= 0 ? G : chalk.red;
        const todayColor = g.stats.todayPnlUsd >= 0 ? G : chalk.red;

        console.log(`  ${W(name)}  ${alloc}  ${rts}  ${pnlColor(pnl)}  ${todayColor(todayPnl)}  ${fills}`);
      }

      console.log(DIM('  ' + '─'.repeat(72)));

      // Totals
      const totalPnlColor = agg.totalPnlUsd >= 0 ? G : chalk.red;
      const todayTotalColor = agg.todayPnlUsd >= 0 ? G : chalk.red;
      console.log(`  ${BOLD('TOTAL'.padEnd(13))}  ${`$${state.totalAllocation.toFixed(0)}`.padStart(8)}  ${String(agg.totalRoundTrips).padStart(6)}  ${totalPnlColor(`$${agg.totalPnlUsd.toFixed(2)}`.padStart(10))}  ${todayTotalColor(`$${agg.todayPnlUsd.toFixed(2)}`.padStart(10))}  ${String(agg.todayFills).padStart(6)}`);
      console.log();
    });

  // ── grid backtest ──
  grid
    .command('backtest')
    .description('Replay historical Hyperliquid prices through the grid strategy')
    .option('--from <iso>', 'Window start (ISO date). Default: 30d ago.')
    .option('--to <iso>', 'Window end (ISO date). Default: now.')
    .option('--capital <usd>', 'Starting capital in USD', '5000')
    .option('--tokens <list>', 'Comma-separated token list', 'bitcoin,ethereum,solana')
    .option('--leverage <n>', 'Override leverage')
    .option('--levels <n>', 'Override levels per side')
    .option('--atr-multiplier <n>', 'Override ATR multiplier')
    .option('--rebalance-drift <n>', 'Override rebalanceDriftPct')
    .option('--snapshot-every <min>', 'Equity-curve snapshot cadence (minutes)', '60')
    .option('--verbose', 'Print manager fill logs during replay')
    .option('--no-cache', 'Skip cache; always fetch fresh data')
    .option('--out <path>', 'Override output path')
    .option('--fee-bps <n>', 'Trading fee in basis points per fill (default 5 = 0.05%)', '5')
    .option('--no-hedge', 'Disable hedge simulation (hedge is ON by default to match live grid)')
    .option('--maintenance-pct <n>', 'Per-token maintenance margin fraction (default 0.02 = 2%, typical Hyperliquid)', '0.02')
    .option('--pause-threshold <n>', 'Pool drop fraction that triggers pause (default 0.40, may be too lenient post leverage-fix)')
    .option('--unpause-recovery <n>', 'Pool drop fraction below which paused grid resumes (default 0.10)')
    .option('--token-split <pairs>', 'Comma-separated token=weight pairs (must sum to 1.0). Default: equal-weight or 0.45/0.30/0.25 for BTC/ETH/SOL.')
    .option('--stop-loss <n>', 'Per-fill stop-loss fraction (default 0.10 = 10%, 0 disables)')
    .action(async (opts) => {
      const now = Date.now();
      const toMs = opts.to ? Date.parse(opts.to) : now;
      const fromMs = opts.from ? Date.parse(opts.from) : (now - 30 * 24 * 3600_000);
      if (Number.isNaN(toMs) || Number.isNaN(fromMs)) {
        throw new Error('--from / --to must be ISO dates (e.g. 2026-04-01)');
      }

      const tokens = (opts.tokens as string).split(',').map(t => t.trim());
      let tokenSplit: Record<string, number>;
      if (opts.tokenSplit) {
        tokenSplit = parseTokenSplit(opts.tokenSplit as string, tokens);
      } else {
        const weight = 1 / tokens.length;
        tokenSplit = {};
        for (const t of tokens) tokenSplit[t] = weight;
      }

      const config: GridConfig = {
        ...DEFAULT_GRID_CONFIG,
        tokens,
        tokenSplit,
        leverage: opts.leverage ? Number(opts.leverage) : DEFAULT_GRID_CONFIG.leverage,
        levelsPerSide: opts.levels ? Number(opts.levels) : DEFAULT_GRID_CONFIG.levelsPerSide,
        atrMultiplier: opts.atrMultiplier ? Number(opts.atrMultiplier) : DEFAULT_GRID_CONFIG.atrMultiplier,
        rebalanceDriftPct: opts.rebalanceDrift ? Number(opts.rebalanceDrift) : DEFAULT_GRID_CONFIG.rebalanceDriftPct,
        maintenanceMarginPct: opts.maintenancePct ? Number(opts.maintenancePct) : DEFAULT_GRID_CONFIG.maintenanceMarginPct,
        pauseThresholdPct: opts.pauseThreshold ? Number(opts.pauseThreshold) : DEFAULT_GRID_CONFIG.pauseThresholdPct,
        unpauseRecoveryPct: opts.unpauseRecovery ? Number(opts.unpauseRecovery) : DEFAULT_GRID_CONFIG.unpauseRecoveryPct,
        stopLossPct: opts.stopLoss !== undefined ? Number(opts.stopLoss) : DEFAULT_GRID_CONFIG.stopLossPct,
      };

      const capital = Number(opts.capital);
      const snapshotEveryMinutes = Number(opts.snapshotEvery);

      console.log();
      console.log(G.bold('  Grid Backtest'));
      SEP();
      console.log(W(`  Window:    ${new Date(fromMs).toISOString().slice(0, 10)} → ${new Date(toMs).toISOString().slice(0, 10)}`));
      console.log(W(`  Capital:   $${capital.toLocaleString()}`));
      console.log(W(`  Tokens:    ${tokens.join(', ')}`));
      console.log(W(`  Leverage:  ${config.leverage}x`));
      console.log(W(`  Levels:    ${config.levelsPerSide}/side`));
      SEP();

      const result = await runBacktest({
        fromMs,
        toMs,
        capital,
        config,
        snapshotEveryMinutes,
        verbose: !!opts.verbose,
        noCache: opts.cache === false,
        outPath: opts.out,
        feeBps: Number(opts.feeBps),
        hedge: opts.hedge !== false,
      });

      printSummary(result);
    });

  // ── grid sweep ──
  grid
    .command('sweep')
    .description('Run a parameter sweep over multiple grid configurations')
    .option('--from <iso>', 'Window start (ISO date). Default: 30d ago.')
    .option('--to <iso>', 'Window end (ISO date). Default: now.')
    .option('--capital <usd>', 'Starting capital in USD', '5000')
    .option('--tokens <list>', 'Comma-separated token list', 'bitcoin,ethereum,solana')
    .option('--leverage <list>', 'Comma-separated leverage values to sweep')
    .option('--levels <list>', 'Comma-separated levels-per-side values to sweep')
    .option('--atr-multiplier <list>', 'Comma-separated ATR multiplier values to sweep')
    .option('--rebalance-drift <list>', 'Comma-separated rebalance drift values to sweep')
    .option('--fee-bps <n>', 'Trading fee in basis points per fill (default 5)', '5')
    .option('--no-cache', 'Skip cache; always fetch fresh data')
    .option('--token-split <pairs>', 'Comma-separated token=weight pairs (must sum to 1.0). Default: equal-weight or 0.45/0.30/0.25 for BTC/ETH/SOL.')
    .action(async (opts) => {
      const now = Date.now();
      const toMs = opts.to ? Date.parse(opts.to) : now;
      const fromMs = opts.from ? Date.parse(opts.from) : (now - 30 * 24 * 3600_000);
      if (Number.isNaN(toMs) || Number.isNaN(fromMs)) {
        throw new Error('--from / --to must be ISO dates (e.g. 2026-04-01)');
      }
      const tokens = (opts.tokens as string).split(',').map(t => t.trim());

      const parseList = (s: string | undefined): number[] | undefined => {
        if (!s) return undefined;
        return s.split(',').map(v => Number(v.trim())).filter(n => Number.isFinite(n));
      };

      const sweep: Partial<Record<SweepableField, number[]>> = {
        leverage: parseList(opts.leverage),
        levelsPerSide: parseList(opts.levels),
        atrMultiplier: parseList(opts.atrMultiplier),
        rebalanceDriftPct: parseList(opts.rebalanceDrift),
      };

      const tokenSplit = opts.tokenSplit
        ? parseTokenSplit(opts.tokenSplit as string, tokens)
        : undefined;

      const result = await runSweep({
        fromMs,
        toMs,
        capital: Number(opts.capital),
        tokens,
        tokenSplit,
        sweep,
        feeBps: Number(opts.feeBps),
        noCache: opts.cache === false,
      });

      printSweepSummary(result);
    });

  // ── grid pause ──
  grid
    .command('pause')
    .description('Manually pause the live grid (sets state.paused=true)')
    .option('--reason <text>', 'Reason for the pause (shown in status output)', DEFAULT_PAUSE_REASON)
    .option('--state-dir <path>', 'Read/write state from a custom directory (defaults to ~/.sherwood/grid)')
    .action(async (opts) => {
      const portfolio = new GridPortfolio(opts.stateDir as string | undefined);
      const state = await portfolio.load();
      if (!state) {
        console.log(DIM('\n  No grid portfolio found. Nothing to pause.\n'));
        return;
      }
      if (state.paused) {
        console.log(DIM(`\n  Grid already paused: ${state.pauseReason}\n`));
        return;
      }
      state.paused = true;
      state.pauseReason = opts.reason as string;
      await portfolio.save(state);
      console.log();
      console.log(chalk.yellow(`  Grid paused. Reason: ${state.pauseReason}`));
      console.log(DIM('  Next manager.tick will be a no-op until you run `sherwood grid resume`.'));
      console.log();
    });

  // ── grid resume ──
  grid
    .command('resume')
    .description('Resume a paused grid (clears state.paused)')
    .option('--state-dir <path>', 'Read/write state from a custom directory (defaults to ~/.sherwood/grid)')
    .action(async (opts) => {
      const portfolio = new GridPortfolio(opts.stateDir as string | undefined);
      const state = await portfolio.load();
      if (!state) {
        console.log(DIM('\n  No grid portfolio found. Nothing to resume.\n'));
        return;
      }
      if (!state.paused) {
        console.log(DIM('\n  Grid is not paused. Nothing to do.\n'));
        return;
      }
      const wasReason = state.pauseReason;
      const wasCircuitBroken = state.circuitBroken === true;
      state.paused = false;
      state.pauseReason = '';
      // Clear the hard circuit breaker if it was tripped — manual `resume`
      // is the documented way to re-engage trading after the deep-drawdown
      // halt. We do NOT auto-clear elsewhere.
      state.circuitBroken = false;
      state.circuitBrokenAt = 0;
      await portfolio.save(state);
      console.log();
      if (wasCircuitBroken) {
        console.log(chalk.yellow(`  Hard circuit breaker cleared.`));
      }
      console.log(G(`  Grid resumed. (was paused: ${wasReason || '<unknown>'})`));
      console.log();
    });

  // ── grid flatten ──
  // Wind a live grid down to no-orders + zero-hedge state, then pause it so
  // the next `start` won't re-enter. Pairs with stopping the systemd unit or
  // killing the loop process — this only cleans up state and (on-chain only)
  // cancels open CLOIDs via the strategy contract.
  grid
    .command('flatten')
    .description('Cancel all open grid orders on-chain, zero the hedge ledger, and pause the grid')
    .option('--state-dir <path>', 'Read/write state from a custom directory (defaults to ~/.sherwood/grid)')
    .option('--strategy <address>', 'Strategy contract address — required to cancel on-chain CLOIDs. Omit for hedge-only flatten.')
    .option('--asset-indices <pairs>', 'comma-separated token=index pairs (required with --strategy, e.g. bitcoin=3,ethereum=4,solana=5)')
    .option('--reason <text>', 'Reason recorded on the paused grid state', DEFAULT_FLATTEN_REASON)
    .option('--dry-run', 'Print intended actions and exit without touching state or chain')
    .option('--yes', 'Skip the confirmation prompt')
    .action(async (opts) => {
      const stateDir = opts.stateDir as string | undefined;
      const portfolio = new GridPortfolio(stateDir);
      const state = await portfolio.load();
      if (!state) {
        console.log(DIM('\n  No grid portfolio found. Nothing to flatten.\n'));
        return;
      }

      // Summarize what we're about to touch — by-token open-fill counts come
      // from the in-memory portfolio rather than re-reading on-chain because
      // (a) the on-chain executor's placedCloids is the local truth, (b) HL
      // order state can lag a tick. The chain call below handles the actual
      // cancel encoding regardless.
      const openFills = state.grids.flatMap(g => g.openFills.filter(f => !f.closed));
      const hedgeMgr = new GridHedgeManager(stateDir);
      const hedgeState = await hedgeMgr.load();
      const openHedges = hedgeState.positions.filter(p => p.quantity > 0);

      let strategyAddress: `0x${string}` | undefined;
      let assetIndices: Record<string, number> | undefined;
      if (opts.strategy) {
        strategyAddress = opts.strategy as `0x${string}`;
        if (!opts.assetIndices) {
          throw new Error('--asset-indices required with --strategy (e.g. --asset-indices bitcoin=3,ethereum=4,solana=5)');
        }
        assetIndices = parseAssetIndices(opts.assetIndices as string);
      }

      console.log();
      console.log(BOLD('  Flatten plan'));
      SEP();
      console.log(W(`  State dir:     ${stateDir ?? '~/.sherwood/grid (default)'}`));
      console.log(W(`  On-chain:      ${strategyAddress ? strategyAddress : DIM('(skipped — no --strategy)')}`));
      console.log(W(`  Open fills:    ${openFills.length} (informational — on-chain CLOIDs cancelled per-token)`));
      console.log(W(`  Hedge legs:    ${openHedges.length}${openHedges.length > 0 ? ' (' + openHedges.map(h => h.token).join(', ') + ')' : ''}`));
      console.log(W(`  Will pause:    yes — reason: "${opts.reason}"`));
      SEP();

      if (opts.dryRun) {
        console.log(chalk.yellow('  --dry-run set — no state or chain mutation.'));
        console.log();
        return;
      }

      if (!opts.yes) {
        // Non-interactive cron / piped invocation must pass --yes; refuse to
        // touch live state without explicit consent so a stray invocation
        // can't quietly wipe a running strategy.
        if (!process.stdin.isTTY) {
          throw new Error('Refusing to flatten without --yes in a non-interactive context. Pass --yes to confirm.');
        }
        // readline gives line-buffered semantics that work even if stdin was
        // touched earlier in the process. AbortController guards against an
        // operator walking away from the prompt — auto-abort after the
        // CONFIRM_TIMEOUT_SECONDS window rather than hanging forever.
        const rl = createInterface({ input: process.stdin, output: process.stdout });
        const ac = new AbortController();
        const timer = setTimeout(() => ac.abort(), CONFIRM_TIMEOUT_SECONDS * 1000);
        let answer: string;
        try {
          answer = (await rl.question(chalk.yellow('  Proceed with flatten? [y/N] '), { signal: ac.signal })).trim().toLowerCase();
        } catch (e) {
          if ((e as { name?: string }).name === 'AbortError') {
            console.log(DIM(`\n  Timed out after ${CONFIRM_TIMEOUT_SECONDS}s. Aborted.\n`));
            return;
          }
          throw e;
        } finally {
          clearTimeout(timer);
          rl.close();
        }
        if (answer !== 'y' && answer !== 'yes') {
          console.log(DIM('  Aborted.\n'));
          return;
        }
      }

      // On-chain cancel — only when a strategy address is supplied. A
      // hedge-only flatten (no --strategy) is the right path for paper grids
      // or when the wallet has been emptied and there's nothing to cancel.
      if (strategyAddress && assetIndices) {
        const executor = new OnchainGridExecutor({ strategyAddress, assetIndices, stateDir });
        await executor.load();
        const res = await executor.flattenAll();
        if (res.cancelled.length > 0) {
          console.log(G(`  Cancelled CLOIDs for: ${res.cancelled.join(', ')}`));
          for (const tx of res.txs) console.log(DIM(`    tx ${tx}`));
        }
        if (res.skipped.length > 0) {
          console.log(DIM(`  Skipped (no tracked CLOIDs): ${res.skipped.join(', ')}`));
        }
        if (res.errors.length > 0) {
          for (const err of res.errors) console.log(chalk.red(`  Cancel error: ${err}`));
        }
      }

      // Zero the hedge ledger using current HL mark prices. If price-fetch
      // fails for a token we leave its position untouched and surface it —
      // operator can rerun with a working RPC or manually edit hedge.json.
      if (openHedges.length > 0) {
        const hl = new HyperliquidProvider();
        const prices: Record<string, number> = {};
        for (const pos of openHedges) {
          const data = await hl.getHyperliquidData(pos.token);
          if (data?.markPrice && data.markPrice > 0) prices[pos.token] = data.markPrice;
        }
        const hres = await hedgeMgr.flatten(prices);
        if (hres.closed.length > 0) {
          for (const c of hres.closed) {
            const sign = c.realizedPnl >= 0 ? '+' : '-';
            console.log(G(`  Hedge closed ${c.token}: qty ${c.quantity.toFixed(6)} → 0  (${sign}$${Math.abs(c.realizedPnl).toFixed(2)} realized)`));
          }
        }
        if (hres.skipped.length > 0) {
          console.log(chalk.yellow(`  Hedge skipped (no mark price): ${hres.skipped.join(', ')}`));
        }
        console.log(W(`  Total hedge realized PnL: $${hres.totalRealizedPnl.toFixed(2)}`));
      } else {
        console.log(DIM('  No active hedge legs — ledger already flat.'));
      }

      // Pause the grid so a future `start` won't re-enter on the same state.
      if (!state.paused) {
        state.paused = true;
        state.pauseReason = opts.reason as string;
        await portfolio.save(state);
        console.log(chalk.yellow(`  Grid paused. Reason: ${state.pauseReason}`));
      } else {
        console.log(DIM(`  Grid was already paused: ${state.pauseReason}`));
      }

      console.log();
      console.log(G('  Flatten complete. Stop the loop process / systemd unit if still running.'));
      console.log();
    });
}
